import React from 'react';
import { TrendingUp, TrendingDown, MessageSquare, BarChart2, SmilePlus } from 'lucide-react';
import { mockKpiData } from '../../data/mockAnalyticsData';
import KPICard from './KPICard';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import { supabaseClient } from '../../utils/supabaseClient';

interface KPICardsProps {
  isLoading: boolean;
  shopname: string;
  dateRange: { start: Date; end: Date };
}

const KPICards: React.FC<KPICardsProps> = ({ isLoading, shopname, dateRange }) => {
  const [ratingAverage, setRatingAverage] = React.useState<number | null>(null);
  const [prevRatingAverage, setPrevRatingAverage] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);
  const isAllPeriod = dateRange.start.getFullYear() === 2000 && dateRange.start.getMonth() === 0 && dateRange.start.getDate() === 1;
  const [reviewCount, setReviewCount] = React.useState<number | null>(null);
  const [prevReviewCount, setPrevReviewCount] = React.useState<number | null>(null);
  const [replyRate, setReplyRate] = React.useState<number | null>(null);
  const [prevReplyRate, setPrevReplyRate] = React.useState<number | null>(null);

  React.useEffect(() => {
    const fetchAverage = async () => {
      setLoading(true);
      // 現在期間
      let query = supabaseClient
        .from('reviews')
        .select('starrating, reviewdate_data, ownerreplied', { count: 'exact' })
        .eq('shopname', shopname);
      if (dateRange) {
        const now = new Date();
        const daysAgo = Math.floor((now.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
        query = query.lte('reviewdate_data', daysAgo);
      }
      const { data, error } = await query;
      if (!error && data) {
        // 平均評価
        const ratings = data.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
        const avg = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
        setRatingAverage(avg);
        // レビュー数
        setReviewCount(data.length);
        // 返信率
        const replied = data.filter((d: any) => (d.ownerreplied || '').toLowerCase() === 'yes').length;
        const rate = data.length > 0 ? (replied / data.length) * 100 : null;
        setReplyRate(rate);
      } else {
        setRatingAverage(null);
        setReviewCount(null);
        setReplyRate(null);
      }

      // 直前期間
      if (!isAllPeriod && dateRange) {
        const periodDays = Math.floor((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const prevStart = new Date(dateRange.start.getTime() - periodDays * 24 * 60 * 60 * 1000);
        const prevEnd = new Date(dateRange.start.getTime() - 1 * 24 * 60 * 60 * 1000);
        let prevQuery = supabaseClient
          .from('reviews')
          .select('starrating, reviewdate_data, ownerreplied', { count: 'exact' })
          .eq('shopname', shopname)
          .lte('reviewdate_data', Math.floor((new Date().getTime() - prevStart.getTime()) / (1000 * 60 * 60 * 24)))
          .gte('reviewdate_data', Math.floor((new Date().getTime() - prevEnd.getTime()) / (1000 * 60 * 60 * 24)));
        const { data: prevData, error: prevError } = await prevQuery;
        if (!prevError && prevData) {
          // 平均評価
          const prevRatings = prevData.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
          const prevAvg = prevRatings.length > 0 ? prevRatings.reduce((a, b) => a + b, 0) / prevRatings.length : null;
          setPrevRatingAverage(prevAvg);
          // レビュー数
          setPrevReviewCount(prevData.length);
          // 返信率
          const prevReplied = prevData.filter((d: any) => (d.ownerreplied || '').toLowerCase() === 'yes').length;
          const prevRate = prevData.length > 0 ? (prevReplied / prevData.length) * 100 : null;
          setPrevReplyRate(prevRate);
        } else {
          setPrevRatingAverage(null);
          setPrevReviewCount(null);
          setPrevReplyRate(null);
        }
      } else {
        setPrevRatingAverage(null);
        setPrevReviewCount(null);
        setPrevReplyRate(null);
      }
      setLoading(false);
    };
    if (shopname) fetchAverage();
  }, [shopname, dateRange]);

  let change = 0;
  if (!isAllPeriod && ratingAverage !== null && prevRatingAverage !== null && prevRatingAverage !== 0) {
    change = ((ratingAverage - prevRatingAverage) / prevRatingAverage) * 100;
  }
  let reviewChange = 0;
  if (!isAllPeriod && reviewCount !== null && prevReviewCount !== null && prevReviewCount !== 0) {
    reviewChange = ((reviewCount - prevReviewCount) / prevReviewCount) * 100;
  }
  let replyChange = 0;
  if (!isAllPeriod && replyRate !== null && prevReplyRate !== null && prevReplyRate !== 0) {
    replyChange = ((replyRate - prevReplyRate) / prevReplyRate) * 100;
  }

  if (isLoading || loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <LoadingSkeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <KPICard
        title="平均評価"
        value={ratingAverage !== null ? ratingAverage.toFixed(1) : '-'}
        previousValue={isAllPeriod ? '' : prevRatingAverage !== null ? prevRatingAverage.toFixed(1) : '-'}
        change={isAllPeriod ? 0 : change}
        icon={TrendingUp}
        iconColor={'text-green-500'}
        format="★"
        hideComparison={isAllPeriod}
        barRatio={3}
      />
      <KPICard
        title="レビュー数"
        value={reviewCount !== null ? reviewCount.toString() : '-'}
        previousValue={isAllPeriod ? '' : prevReviewCount !== null ? prevReviewCount.toString() : '-'}
        change={isAllPeriod ? 0 : reviewChange}
        icon={BarChart2}
        iconColor="text-blue-500"
        format="件"
        hideComparison={isAllPeriod}
        barRatio={1}
      />
      
      <KPICard
        title="返信率"
        value={replyRate !== null ? replyRate.toFixed(1) : '-'}
        previousValue={isAllPeriod ? '' : prevReplyRate !== null ? prevReplyRate.toFixed(1) : '-'}
        change={isAllPeriod ? 0 : replyChange}
        icon={SmilePlus}
        iconColor={replyChange >= 0 ? 'text-green-500' : 'text-red-500'}
        format="%"
        hideComparison={isAllPeriod}
        barRatio={1}
      />
    </div>
  );
};

export default KPICards;